import { ProductCardImage } from "@/shared";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPrevButton,
} from "@dafiti/closet-ui";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { useCallback } from "react";

import type { CatalogProduct } from "../../../types/catalog.type";

const arrowClassName = cn(
  "size-[24px] bg-white [&_svg]:size-[8px] text-black hover:bg-gray-100 active:bg-white",
  "disabled:opacity-0 opacity-0 group-hover:opacity-100 border-gray-300 border-[1px]",
);

export function CatalogProductCardImage({
  product,
  productIndex,
  imageOverlaySlot,
}: {
  product: Pick<CatalogProduct, "images" | "name">;
  productIndex?: number;
  imageOverlaySlot?: ReactNode;
}) {
  const t = useTranslations("catalog");
  const getImageLoadingPriority = useCallback(
    (imageIndex: number) => {
      if (imageIndex > 0 || (productIndex && productIndex > 10)) {
        return "lazy";
      }

      return "eager";
    },
    [productIndex],
  );

  return (
    <div className="relative">
      <div className="rounded-lg overflow-hidden h-auto aspect-[131/190] flex justify-center items-center relative">
        <Carousel className="size-full">
          <CarouselContent
            viewportClassName="h-full gap-0 bg-gray-100"
            containerClassName="h-full gap-0"
          >
            {product.images.map((image, index) => (
              <CarouselItem key={image.url} className="h-full">
                <ProductCardImage
                  src={image.url}
                  alt={product.name}
                  loading={getImageLoadingPriority(index)}
                  containerClassName="h-full rounded-none"
                />
              </CarouselItem>
            ))}
          </CarouselContent>

          <div
            className="absolute left-2 top-1/2 -translate-y-1/2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <CarouselPrevButton
              className={arrowClassName}
              ariaLabel={t("productCard.previousImage")}
            />
          </div>

          <div
            className="absolute right-2 top-1/2 -translate-y-1/2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <CarouselNextButton
              className={arrowClassName}
              ariaLabel={t("productCard.nextImage")}
            />
          </div>
        </Carousel>
        {imageOverlaySlot}
      </div>
    </div>
  );
}
